// Service for handling Deepgram API calls through backend

import { 
  DeepgramResponse, 
  SpeakerLabels,
  VideoSourceInfo
} from '../types';

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : 'http://localhost:7777/api';

export interface CacheInfo {
  hit: boolean;
  checksum: string;
  cachedAt: string;
}

export interface CachedDeepgramResponse extends DeepgramResponse {
  _cache?: CacheInfo;
  _speakerLabels?: SpeakerLabels;
  _s3AudioKey?: string | null;
  _audioFileSize?: number | null;
  _audioMimeType?: string | null;
  _sourceUrl?: string | null;
  _videoSourceInfo?: VideoSourceInfo;
}

export interface LogEntry {
  id: number;
  user_email: string;
  action: string;
  details: string;
  timestamp: string;
}

export interface ActivityLogResponse {
  logs: LogEntry[];
}

export interface AccessRequest {
  id: number;
  user_email: string;
  status: string;
  requested_at: string;
}

export interface AccessRequestsResponse {
  requests: AccessRequest[];
}

export interface ManageRequestResponse {
  message: string;
}

export class DeepgramService {
  private static async request<T>(endpoint: string, options: RequestInit = {}, token?: string): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const headers = new Headers(options.headers);
    headers.set('Content-Type', 'application/json');

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const response = await fetch(url, {
      headers,
      ...options,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Network error' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  static async transcribeFile(file: File, settings: any, token?: string): Promise<CachedDeepgramResponse> {
    const formData = new FormData();
    formData.append('audio', file);
    formData.append('settings', JSON.stringify(settings));

    console.log('Sending transcription request for file:', file.name, 'Size:', file.size);

    const headers = new Headers();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const response = await fetch(`${API_BASE_URL}/transcribe`, {
      method: 'POST',
      body: formData,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Network error' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    const result = await response.json();
    
    // Log cache information if available
    if (result._cache) {
      if (result._cache.hit) {
        console.log(`✅ Cache HIT - Used cached transcription from ${result._cache.cachedAt}`);
      } else {
        console.log(`🔄 Cache MISS - New transcription cached at ${result._cache.cachedAt}`);
      }
    }

    return result;
  }

  static async transcribeUrl(url: string, settings: any, token?: string): Promise<CachedDeepgramResponse> {
    const result = await this.request<CachedDeepgramResponse>('/transcribe-url', {
      method: 'POST',
      body: JSON.stringify({ url, settings }),
    }, token);

    // Log cache information if available
    if (result._cache) {
      if (result._cache.hit) {
        console.log(`✅ Cache HIT - Used cached transcription from ${result._cache.cachedAt}`);
      } else {
        console.log(`🔄 Cache MISS - New transcription cached at ${result._cache.cachedAt}`);
      }
    }

    return result;
  }

  static async transcribeYoutube(url: string, settings: any, token?: string): Promise<CachedDeepgramResponse> {
    console.log('Sending YouTube transcription request for URL:', url);
    
    // Get cookies from window if available
    const cookies = (window as any).__youtubeCookies;
    
    const result = await this.request<CachedDeepgramResponse>('/transcribe-youtube', {
      method: 'POST',
      body: JSON.stringify({ url, settings, cookies }),
    }, token);

    // Log cache information if available
    if (result._cache) {
      if (result._cache.hit) {
        console.log(`✅ YouTube Cache HIT - Used cached transcription from ${result._cache.cachedAt}`);
      } else {
        console.log(`🔄 YouTube Cache MISS - New transcription cached at ${result._cache.cachedAt}`);
      }
    }

    return result;
  }

  static async transcribeDropbox(
    url: string,
    settings: any,
    options: { videoFilename?: string } = {},
    token?: string
  ): Promise<CachedDeepgramResponse> {
    console.log('Sending Dropbox transcription request for URL:', url);

    const result = await this.request<CachedDeepgramResponse>('/transcribe-dropbox', {
      method: 'POST',
      body: JSON.stringify({
        url,
        settings,
        videoFilename: options.videoFilename,
      }),
    }, token);

    if (result._cache) {
      if (result._cache.hit) {
        console.log(`✅ Dropbox Cache HIT - Used cached transcription from ${result._cache.cachedAt}`);
      } else {
        console.log(`🔄 Dropbox Cache MISS - New transcription cached at ${result._cache.cachedAt}`);
      }
    }

    return result;
  }

  static async saveSpeakerLabels(checksum: string, labels: SpeakerLabels, token?: string): Promise<void> {
    await this.request(`/cache/metadata/${checksum}`, {
        method: 'POST',
        body: JSON.stringify({ speakerLabels: labels }),
    }, token);
  }

  static async getCacheStats(token?: string) {
    return this.request('/cache-stats', {}, token);
  }

  static async clearCache(token?: string) {
    return this.request('/cache-clear', { method: 'POST' }, token);
  }

  static async getCacheList(token?: string) {
    return this.request('/cache-list', {}, token);
  }

  static async getActivityLog(token: string): Promise<ActivityLogResponse> {
    return this.request<ActivityLogResponse>('/activity-log', {}, token);
  }

  static async getAccessRequests(token: string): Promise<AccessRequestsResponse> {
    return this.request<AccessRequestsResponse>('/access-requests', {}, token);
  }

  static async manageAccessRequest(token: string, requestId: number, action: 'approve' | 'reject'): Promise<ManageRequestResponse> {
    return this.request<ManageRequestResponse>('/manage-request', {
      method: 'POST',
      body: JSON.stringify({ requestId, action }),
    }, token);
  }

  static async requestAccess(token: string) {
    // This uses the public /request-access endpoint, so it doesn't need the auth token in the header
    // The token is sent in the body for verification
    const response = await fetch(`${API_BASE_URL}/request-access`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Network error' }));
      throw new Error(errorData.message || `HTTP ${response.status}`);
    }
    return response.json();
  }

  static async healthAuth(token: string) {
    return this.request('/health-auth', {}, token);
  }

  // API Key management methods for compatibility
  // Note: In this architecture, API keys are managed on the backend
  static isConfigured(): boolean {
    // Since API keys are managed on the backend, we assume they're configured
    return true;
  }

  static async validateApiKey(apiKey: string, token?: string): Promise<boolean> {
    // API key validation is handled by the backend
    try {
      await this.request('/validate-key', {
        method: 'POST',
        body: JSON.stringify({ apiKey }),
      }, token);
      return true;
    } catch {
      return false;
    }
  }

  static initializeClient(apiKey: string): void {
    // Client initialization is handled by the backend
    console.log('API key would be set on backend');
  }

  static clearApiKey(): void {
    // API key clearing is handled by the backend
    console.log('API key would be cleared on backend');
  }
}

export const deepgramService = DeepgramService;
